import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHero, Reveal, SectionEyebrow } from "@/components/Primitives";
import { fetchInsights } from "@/lib/api";
import { Search, Clock, ArrowUpRight } from "lucide-react";
import SEO from "@/lib/seo";

export default function Insights() {
  const [posts, setPosts] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    fetchInsights({ q: q || undefined }).then(setPosts);
  }, [q]);

  const [featured, ...rest] = posts;

  return (
    <div data-testid="insights-page">
      <SEO title="Insights · The Cleanability Institute" description="Essays, research notes and commentary from the Institute." path="/insights" />
      <PageHero
        testId="insights-hero"
        eyebrow="Insights"
        title="Essays, research notes and commentary from the Institute."
        lead="A growing body of writing on cleanability, facilities management, healthcare hygiene, sustainability, AI in operations, invisible labour, and the future of work in the built environment."
      />

      <section className="container-x border-t border-border py-6 sticky top-16 md:top-20 z-30 bg-background/85 backdrop-blur-xl">
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:justify-between">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              data-testid="insights-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search essays, research, topics…"
              className="w-full h-10 pl-9 pr-3 rounded-md border border-border bg-card text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]"
            />
          </div>
          <Link to="/insights/submit" data-testid="insights-submit-cta" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 whitespace-nowrap">
            Submit your essay <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>

      {featured && (
        <section className="container-x py-16">
          <Reveal>
            <Link to={`/insights/${featured.slug}`} data-testid="insights-featured" className="block rounded-3xl border border-border bg-card p-10 md:p-16 hover-lift group cursor-pointer">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                <div className="lg:col-span-8">
                  <div className="font-mono-mini text-[hsl(var(--accent))]">{featured.category} · Featured</div>
                  <h2 className="mt-4 font-display text-3xl md:text-5xl lg:text-6xl tracking-tight font-medium text-balance">
                    {featured.title}
                  </h2>
                  <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
                    {featured.excerpt}
                  </p>
                </div>
                <div className="lg:col-span-4 flex flex-col gap-3">
                  <div className="text-sm">
                    <div className="font-display font-medium">{featured.author}</div>
                    <div className="text-xs text-muted-foreground">{featured.published_at}</div>
                  </div>
                  <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" /> {featured.read_minutes} min read
                  </div>
                  <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium underline-link">
                    Read essay <ArrowUpRight className="h-4 w-4" />
                  </div>
                </div>
              </div>
            </Link>
          </Reveal>
        </section>
      )}

      <section className="container-x pb-24">
        <SectionEyebrow>All writing</SectionEyebrow>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((post, i) => (
            <Reveal key={post.id} delay={i * 0.04}>
              <Link to={`/insights/${post.slug}`} data-testid={`insight-${i}`} className="rounded-2xl border border-border bg-card p-7 hover-lift h-full flex flex-col group cursor-pointer">
                <div className="font-mono-mini text-[hsl(var(--accent))]">{post.category}</div>
                <h3 className="mt-4 font-display text-xl md:text-2xl font-medium tracking-tight text-balance">{post.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{post.excerpt}</p>
                <div className="mt-8 pt-6 border-t border-border flex items-center justify-between">
                  <div>
                    <div className="text-xs font-medium">{post.author}</div>
                    <div className="text-[10px] text-muted-foreground">{post.published_at}</div>
                  </div>
                  <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" /> {post.read_minutes} min
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
